import React, { memo, useEffect, useMemo, useState } from 'react';
import { DealResponse } from '@business/entities/deal';
import { TmRegionResponse } from '@business/entities/tm/TmRegionResponse';
import { DealLocationMap } from '@modules/deals/AddEditDealModal/DealLocationMap';
import { InfoBanner } from '@modules/common/InfoBanner';
import { GoogleAPI } from '@business/api/google_api';

export interface DealRegionMapProps {
  deal: DealResponse;
  regions: TmRegionResponse[];
}

// Same map as the Deal Creation page (shared DealLocationMap), scoped to what matters for
// this deal: the customer's property pin, the region the ADDRESS belongs to, and the
// assigned sales rep's boundary (highlighted). Available before a salesperson is assigned.
export const DealRegionMap = memo((props: DealRegionMapProps) => {
  const { deal, regions } = props;
  const owner = deal.owner as any;

  const defaultAddress = useMemo(() => {
    const addresses = ((deal.organization as any)?.addresses ?? []) as any[];
    return addresses.find(a => a.isDefault) ?? addresses[0] ?? null;
  }, [deal.organization]);

  // Org addresses store no coordinates — geocode the default address on demand,
  // exactly like the Deal Creation form does while typing.
  const [propertyPin, setPropertyPin] = useState<{ lat: number; lng: number } | null>(null);
  useEffect(() => {
    setPropertyPin(null);
    if (!defaultAddress) return;

    const text = [defaultAddress.address, defaultAddress.city, defaultAddress.state, defaultAddress.postal]
      .filter(Boolean)
      .join(', ');
    if (!text) return;

    const sub = GoogleAPI.geocodeAddress(text).subscribe(res => {
      if (res.lat != null && res.lng != null) setPropertyPin({ lat: res.lat, lng: res.lng });
    });
    return () => sub.unsubscribe();
  }, [defaultAddress]);

  // The region the customer's ADDRESS is assigned to (drawn as context).
  const addressRegion = useMemo((): TmRegionResponse | null => {
    const regionId = defaultAddress?.regionId ?? defaultAddress?.region?.id;
    if (!regionId) return null;
    const region = regions.find(r => String(r.id) === String(regionId)) ?? null;
    return region?.geoJson ? region : null;
  }, [defaultAddress, regions]);

  // Assigned rep's boundary (highlighted): their own drawn territory first, else their region.
  const mapRegion = useMemo((): TmRegionResponse | null => {
    if (deal.ownerGeoJson && owner) {
      return { id: owner.id, name: owner.name, geoJson: deal.ownerGeoJson };
    }
    if (owner?.regionId) {
      return regions.find(r => String(r.id) === String(owner.regionId)) ?? null;
    }
    return null;
  }, [deal.ownerGeoJson, owner, regions]);

  const hasAnythingToShow = !!propertyPin || !!addressRegion || !!mapRegion;

  return (
    <div className="p-3">
      {owner && !mapRegion && (
        <InfoBanner intent="info">
          <strong>{owner.name}</strong> has no territory boundary set. Open the user profile, select their Region, and draw their territory boundary.
        </InfoBanner>
      )}
      {!hasAnythingToShow && (
        <InfoBanner intent="info">No customer address or territory available to display on the map.</InfoBanner>
      )}
      <DealLocationMap
        addressLat={propertyPin?.lat}
        addressLng={propertyPin?.lng}
        region={mapRegion}
        regions={addressRegion ? [addressRegion] : []}
        height={520}
      />
    </div>
  );
});
